import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  User,
  Clock,
  ChevronRight,
  ArrowLeft,
  Share2,
  Facebook,
  Linkedin,
  Link as LinkIcon,
  Leaf,
  Recycle,
  Sparkles,
} from "lucide-react";
import plasticDetoxImg from "../assets/images/plastic_detox.jpg";
import microplasticImg from "../assets/images/microplastic.png";
import edibleCupImg from "../assets/images/edible_cups.jpg";
import cutleryImg from "../assets/images/home_cutlery.jpg";

// X (formerly Twitter) icon
const XIcon = ({ size = 18 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const POST_META = {
  title: "Plastic Detox: Rethinking Everyday Waste",
  description:
    "The Netflix documentary The Plastic Detox shows how single-use plastic harms our planet and how reusable, biodegradable, and edible alternatives are reshaping the future of packaging.",
  author: "Magomeal Team",
  date: "May 8, 2026",
  isoDate: "2026-05-08",
  readTime: "5 min read",
  category: "Sustainability",
  slug: "/blog/plastic-detox-rethinking-everyday-waste",
  ogImage: "/assets/plastic_detox.jpg",
};

const useDocumentMeta = () => {
  useEffect(() => {
    const previousTitle = document.title;
    const fullUrl = `${window.location.origin}${POST_META.slug}`;
    const ogImageUrl = `${window.location.origin}${POST_META.ogImage}`;

    document.title = `${POST_META.title} | Magomeal Blog`;

    const setMeta = (selector, attr, name, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };

    const created = [];
    const ensure = (selector, attr, name, content) => {
      const existed = !!document.head.querySelector(selector);
      const el = setMeta(selector, attr, name, content);
      if (!existed) created.push(el);
    };

    ensure(
      'meta[name="description"]',
      "name",
      "description",
      POST_META.description
    );
    ensure(
      'meta[name="keywords"]',
      "name",
      "keywords",
      "plastic detox, plastic pollution, edible cups, edible cutlery, sustainable packaging, eco friendly, biodegradable, zero waste"
    );

    // Open Graph
    ensure('meta[property="og:type"]', "property", "og:type", "article");
    ensure('meta[property="og:title"]', "property", "og:title", POST_META.title);
    ensure(
      'meta[property="og:description"]',
      "property",
      "og:description",
      POST_META.description
    );
    ensure('meta[property="og:image"]', "property", "og:image", ogImageUrl);
    ensure('meta[property="og:url"]', "property", "og:url", fullUrl);
    ensure(
      'meta[property="og:site_name"]',
      "property",
      "og:site_name",
      "Magomeal"
    );
    ensure(
      'meta[property="article:published_time"]',
      "property",
      "article:published_time",
      POST_META.isoDate
    );
    ensure(
      'meta[property="article:author"]',
      "property",
      "article:author",
      POST_META.author
    );

    // Twitter
    ensure(
      'meta[name="twitter:card"]',
      "name",
      "twitter:card",
      "summary_large_image"
    );
    ensure(
      'meta[name="twitter:title"]',
      "name",
      "twitter:title",
      POST_META.title
    );
    ensure(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      POST_META.description
    );
    ensure('meta[name="twitter:image"]', "name", "twitter:image", ogImageUrl);

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    let canonicalCreated = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      canonicalCreated = true;
    }
    canonical.setAttribute("href", fullUrl);

    // JSON-LD Structured Data
    const ldJson = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: POST_META.title,
      description: POST_META.description,
      image: [ogImageUrl],
      datePublished: POST_META.isoDate,
      dateModified: POST_META.isoDate,
      author: {
        "@type": "Organization",
        name: POST_META.author,
      },
      publisher: {
        "@type": "Organization",
        name: "Magomeal",
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": fullUrl,
      },
    };

    const ldScript = document.createElement("script");
    ldScript.type = "application/ld+json";
    ldScript.id = "ld-json-plastic-detox";
    ldScript.text = JSON.stringify(ldJson);
    const existingLd = document.getElementById("ld-json-plastic-detox");
    if (existingLd) existingLd.remove();
    document.head.appendChild(ldScript);

    return () => {
      document.title = previousTitle;
      created.forEach((el) => el && el.parentNode && el.parentNode.removeChild(el));
      if (canonicalCreated && canonical && canonical.parentNode) {
        canonical.parentNode.removeChild(canonical);
      }
      const ld = document.getElementById("ld-json-plastic-detox");
      if (ld) ld.remove();
    };
  }, []);
};

const BlogPlasticDetox = () => {
  useDocumentMeta();
  const [copied, setCopied] = useState(false);

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${POST_META.slug}`
      : "";
  const shareText = encodeURIComponent(POST_META.title);
  const encodedUrl = encodeURIComponent(shareUrl);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback
      const textarea = document.createElement("textarea");
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (_) {}
      document.body.removeChild(textarea);
    }
  };

  const relatedPosts = [
    {
      title:
        "Microplastics in Disposable Paper Cups: Hidden Risks in Hot Drinks",
      excerpt:
        "How disposable paper cups release microplastics and harmful chemicals when exposed to hot liquids.",
      image: microplasticImg,
      date: "November 23, 2025",
      readTime: "5 min read",
      to: "/blog",
    },
    {
      title: "Are Edible Cups Safe? What They're Made Of and Why They Matter",
      excerpt:
        "What edible cups are made of, how they perform with hot drinks, and why they matter for sustainability.",
      image: edibleCupImg,
      date: "December 29, 2025",
      readTime: "4 min read",
      to: "/blog",
    },
  ];

  return (
    <div className="blog-page plastic-detox-page">
      {/* Breadcrumb */}
      <section className="post-breadcrumb">
        <div className="container">
          <Link to="/blog" className="breadcrumb-link">
            <ArrowLeft size={16} />
            <span>Back to Blog</span>
          </Link>
        </div>
      </section>

      {/* Article Header */}
      <article className="single-post">
        <div className="container">
          <header className="single-post-header">
            <div className="post-category-tag">
              <Leaf size={14} />
              <span>{POST_META.category}</span>
            </div>
            <h1 className="single-post-title">{POST_META.title}</h1>
            <p className="single-post-subtitle">{POST_META.description}</p>

            <div className="single-post-meta">
              <div className="meta-item">
                <User size={16} />
                <span>{POST_META.author}</span>
              </div>
              <div className="meta-item">
                <Calendar size={16} />
                <time dateTime={POST_META.isoDate}>{POST_META.date}</time>
              </div>
              <div className="meta-item">
                <Clock size={16} />
                <span>{POST_META.readTime}</span>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <figure className="single-post-featured">
            <img
              src={plasticDetoxImg}
              alt="Plastic Detox - rethinking everyday waste with sustainable alternatives"
              loading="eager"
              fetchpriority="high"
              decoding="async"
              width="1600"
              height="900"
            />
            <figcaption>
              Single-use plastic is everywhere but it doesn't have to be.
            </figcaption>
          </figure>

          {/* Social Share Top */}
          <div className="single-post-share">
            <span className="share-label">
              <Share2 size={16} /> Share this article
            </span>
            <div className="share-buttons">
              <a
                className="share-btn share-facebook"
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Facebook"
              >
                <Facebook size={18} />
              </a>
              <a
                className="share-btn share-x"
                href={`https://x.com/intent/post?text=${shareText}&url=${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on X"
              >
                <XIcon size={16} />
              </a>
              <a
                className="share-btn share-linkedin"
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on LinkedIn"
              >
                <Linkedin size={18} />
              </a>
              <a
                className="share-btn share-whatsapp"
                href={`https://api.whatsapp.com/send?text=${shareText}%20${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on WhatsApp"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M20.52 3.48A11.86 11.86 0 0 0 12.04 0C5.46 0 .12 5.34.12 11.92c0 2.1.55 4.16 1.6 5.97L0 24l6.27-1.65a11.9 11.9 0 0 0 5.77 1.47h.01c6.58 0 11.92-5.34 11.92-11.92 0-3.18-1.24-6.17-3.45-8.42zM12.05 21.6h-.01a9.66 9.66 0 0 1-4.93-1.35l-.35-.21-3.72.98.99-3.63-.23-.37a9.65 9.65 0 0 1-1.49-5.1c0-5.34 4.35-9.69 9.7-9.69 2.59 0 5.02 1.01 6.85 2.84a9.6 9.6 0 0 1 2.84 6.85c0 5.34-4.35 9.68-9.65 9.68zm5.31-7.25c-.29-.15-1.72-.85-1.99-.95-.27-.1-.46-.15-.66.15-.2.29-.76.95-.93 1.15-.17.2-.34.22-.63.07-.29-.15-1.22-.45-2.33-1.43-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.2-.29.29-.49.1-.2.05-.37-.02-.51-.07-.15-.66-1.59-.9-2.18-.24-.57-.48-.49-.66-.5l-.56-.01c-.2 0-.51.07-.78.37-.27.29-1.03 1.01-1.03 2.46 0 1.45 1.06 2.85 1.21 3.05.15.2 2.08 3.18 5.04 4.46.7.3 1.25.48 1.68.62.71.22 1.35.19 1.86.12.57-.08 1.72-.7 1.96-1.38.24-.68.24-1.27.17-1.38-.07-.12-.27-.2-.56-.34z" />
                </svg>
              </a>
              <button
                className={`share-btn share-copy ${copied ? "copied" : ""}`}
                onClick={handleCopy}
                aria-label="Copy link"
                type="button"
              >
                <LinkIcon size={18} />
                <span className="copy-tooltip">{copied ? "Copied!" : "Copy"}</span>
              </button>
            </div>
          </div>

          {/* Article Body */}
          <div className="single-post-body">
            <p className="lead-paragraph">
              The Netflix documentary{" "}
              <em>The Plastic Detox</em> explores how deeply plastic has become
              part of modern life and the environmental damage it leaves
              behind. From oceans filled with microplastics to overflowing
              landfills, the film highlights how single use plastic products
              are affecting ecosystems, wildlife, and human health.
            </p>

            <p>
              The documentary follows scientists, environmental activists, and
              businesses that are searching for practical alternatives to
              plastic packaging and disposable products. It explains how
              convenience culture has created a global waste problem,
              especially through items designed to be used for only a few
              minutes but that remain in nature for hundreds of years.
            </p>

            <blockquote className="post-blockquote">
              <Sparkles size={20} className="quote-icon" />
              <p>
                Small everyday choices can create meaningful change. Reusable,
                biodegradable, and edible products are presented as innovative
                solutions that reduce waste while encouraging more sustainable
                habits.
              </p>
            </blockquote>

            <p>
              As consumers become more aware of environmental issues,
              businesses are also adapting. Eco friendly alternatives such as
              edible cups, spoons, and cutlery are gaining popularity because
              they help reduce plastic waste without sacrificing convenience.
              These products offer a smarter way to enjoy food and drinks
              while leaving behind little to no environmental footprint.
            </p>

            <p>
              The growing demand for sustainable products shows that the
              future of packaging and dining is changing. Choosing
              alternatives to plastic is no longer just an environmental
              trend. It is becoming an important step toward a cleaner and
              healthier planet.
            </p>

            {/* Key Takeaways */}
            <div className="post-takeaways">
              <h3>
                <Leaf size={18} /> Key Takeaways
              </h3>
              <ul>
                <li>
                  Single-use plastic outlives its purpose by centuries
                  minutes of use, hundreds of years of waste.
                </li>
                <li>
                  Microplastics are now found in oceans, soil, food and even
                  inside the human body.
                </li>
                <li>
                  Edible and biodegradable alternatives offer convenience
                  without the long-term environmental cost.
                </li>
                <li>
                  Consumer choice is driving a real shift in how brands
                  approach packaging.
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Share */}
          <div className="single-post-share single-post-share-bottom">
            <span className="share-label">
              <Share2 size={16} /> Enjoyed this article? Share it
            </span>
            <div className="share-buttons">
              <a
                className="share-btn share-facebook"
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Facebook"
              >
                <Facebook size={18} />
              </a>
              <a
                className="share-btn share-x"
                href={`https://x.com/intent/post?text=${shareText}&url=${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on X"
              >
                <XIcon size={16} />
              </a>
              <a
                className="share-btn share-linkedin"
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on LinkedIn"
              >
                <Linkedin size={18} />
              </a>
              <button
                className={`share-btn share-copy ${copied ? "copied" : ""}`}
                onClick={handleCopy}
                aria-label="Copy link"
                type="button"
              >
                <LinkIcon size={18} />
              </button>
            </div>
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <section className="post-cta-section">
        <div className="container">
          <div className="post-cta-card">
            <div className="post-cta-icon">
              <Recycle size={36} />
            </div>
            <h2>Start your own plastic detox today</h2>
            <p>
              Discover delicious, edible alternatives to plastic cups and
              cutlery. Good for your customers, great for the planet.
            </p>
            <div className="post-cta-actions">
              <Link to="/edible-cups" className="btn-primary">
                Shop Edible Cups <ChevronRight size={18} />
              </Link>
              <Link to="/cutlery-tableware" className="btn-secondary">
                Explore Edible Cutlery <ChevronRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      <section className="related-posts-section">
        <div className="container">
          <h2 className="related-posts-title">Related Articles</h2>
          <div className="related-posts-grid">
            {relatedPosts.map((rp, idx) => (
              <Link key={idx} to={rp.to} className="related-post-card">
                <div className="related-post-image">
                  <img src={rp.image} alt={rp.title} loading="lazy" />
                </div>
                <div className="related-post-content">
                  <h3>{rp.title}</h3>
                  <p>{rp.excerpt}</p>
                  <div className="related-post-meta">
                    <span>
                      <Calendar size={14} /> {rp.date}
                    </span>
                    <span>
                      <Clock size={14} /> {rp.readTime}
                    </span>
                  </div>
                  <div className="related-post-link">
                    Read article <ChevronRight size={16} />
                  </div>
                </div>
              </Link>
            ))}
            <Link to="/cutlery-tableware" className="related-post-card related-post-card-product">
              <div className="related-post-image">
                <img src={cutleryImg} alt="Edible cutlery range" loading="lazy" />
              </div>
              <div className="related-post-content">
                <h3>Edible Cutlery Range</h3>
                <p>
                  A complete plastic-free dining experience spoons, forks,
                  stirrers and more.
                </p>
                <div className="related-post-link">
                  Explore products <ChevronRight size={16} />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPlasticDetox;
